<template>
  <div class="oauth-callback-wrapper">
    <h2>Completing Google sign-in...</h2>
    <md-progress-spinner :md-diameter="40" :md-stroke="4" md-mode="indeterminate"></md-progress-spinner>
    <p v-if="serverError" class="error">{{serverError}}</p>
  </div>
</template>

<script>
import Vue from 'vue'
import { MdProgress } from 'vue-material/dist/components'
import { mapGetters, mapActions } from 'vuex'
import { UserTypeEnum } from 'temporary-api-types/apiTypes'

Vue.use(MdProgress)

const CALLBACK_PATH = '/auth/callback'
const GOOGLE_REDIRECT_URI_STUDENT = process.env.VUE_APP_GOOGLE_REDIRECT_URI_STUDENT
const GOOGLE_REDIRECT_URI_PROFESSOR = process.env.VUE_APP_GOOGLE_REDIRECT_URI_PROFESSOR
const GOOGLE_REDIRECT_URI_DEFAULT = process.env.VUE_APP_GOOGLE_REDIRECT_URI

if(!GOOGLE_REDIRECT_URI_STUDENT || !GOOGLE_REDIRECT_URI_PROFESSOR || !GOOGLE_REDIRECT_URI_DEFAULT) {
  throw new Error('Missing env var')
}

const ensureCallbackPath = (value) => {
  if(!value) {
    return `${window.location.origin}${CALLBACK_PATH}`
  }

  try {
    const parsed = new URL(value)
    if(parsed.pathname === CALLBACK_PATH) {
      return parsed.toString()
    }
    parsed.pathname = CALLBACK_PATH
    parsed.search = ''
    parsed.hash = ''
    return parsed.toString()
  } catch(err) {
    return `${window.location.origin}${CALLBACK_PATH}`
  }
}

const getGoogleRedirectUri = (userType) => {
  if(!GOOGLE_REDIRECT_URI_STUDENT || !GOOGLE_REDIRECT_URI_PROFESSOR || !GOOGLE_REDIRECT_URI_DEFAULT) {
    throw new Error('Missing env var')
  }

  if(userType === UserTypeEnum.Student && GOOGLE_REDIRECT_URI_STUDENT) {
    return ensureCallbackPath(GOOGLE_REDIRECT_URI_STUDENT)
  }

  if((userType === UserTypeEnum.Professor || userType === UserTypeEnum.Admin) && GOOGLE_REDIRECT_URI_PROFESSOR) {
    return ensureCallbackPath(GOOGLE_REDIRECT_URI_PROFESSOR)
  }

  return ensureCallbackPath(GOOGLE_REDIRECT_URI_DEFAULT)
}

export default {
  name: 'OAuthCallback',
  data() {
    return {
      url: process.env.VUE_APP_LAMBDA_URL,
      serverError: ''
    }
  },
  computed: {
    ...mapGetters([
      'getUserType'
    ])
  },
  methods: {
    ...mapActions([
      'setUserToken',
      'setUserRefreshToken',
      'setUserTokenExpTime',
      'fetchSemesterList',
      'setAccessToken',
      'configureUserType',
      'configureUserEmail',
      'validateAdminLicense'
    ]),
    getRedirectUri() {
      return getGoogleRedirectUri(this.getUserType)
    },
    async completeGoogleOAuthLogin(code) {
      const response = await fetch(`${this.url}/googleAuthCode`, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Request-Method': 'POST'
        },
        body: JSON.stringify({
          code,
          groupName: this.getUserType,
          redirectUri: this.getRedirectUri()
        })
      })

      const responseText = await response.text()
      let responseJson = null
      try {
        responseJson = responseText ? JSON.parse(responseText) : {}
      } catch(parseError) {
        responseJson = { message: responseText || 'Google sign-in failed' }
      }

      if(!response.ok) {
        this.serverError = responseJson.message || 'Google sign-in failed'
        if(responseJson.error) {
          console.error('Google Auth Error:', responseJson.error)
        } else {
          console.error('Google Auth Error:', responseJson)
        }
        return
      }

      if(responseJson.requiresOnboard) {
        this.$router.push({
          name: 'Onboard',
          params: {
            showBackButton: true,
            selectedEmail: responseJson.user.email,
            selectedFirstName: responseJson.user.firstname,
            selectedLastName: responseJson.user.lastname,
            notifyMessage: 'Google account verified. Complete onboarding to finish setup.'
          }
        })
        return
      }

      const email = (responseJson.user && responseJson.user.email) || ''
      const token = responseJson.input.AuthenticationResult.IdToken
      const accessToken = responseJson.input.AuthenticationResult.AccessToken
      const tokenExpSeconds = responseJson.input.AuthenticationResult.ExpiresIn
      const refreshToken = responseJson.input.AuthenticationResult.RefreshToken
      const currentTimestamp = Date.now()
      const tokenExpTime = currentTimestamp + (tokenExpSeconds * 1000)

      await this.setUserToken(token)
      await this.setAccessToken(accessToken)
      await this.configureUserEmail(email.toLowerCase())
      await this.setUserTokenExpTime(tokenExpTime)
      await this.setUserRefreshToken(refreshToken)

      if(this.getUserType === UserTypeEnum.Admin) {
        await this.validateAdminLicense({ userEmail: email.toLowerCase() })
        this.$router.push({ name: 'AdminHome' })
      } else {
        await this.fetchSemesterList()
        this.$router.push({ name: 'Semester' })
      }
    },
    async handleGoogleOAuthRedirect() {
      const query = new URLSearchParams(window.location.search)
      const code = query.get('code')
      const oauthError = query.get('error')

      if(!code && !oauthError) {
        this.$router.replace({ name: 'Login' })
        return
      }

      // Strip one-time OAuth params from URL after first parse.
      window.history.replaceState({}, document.title, window.location.pathname)

      if(oauthError) {
        this.serverError = 'Google sign-in was cancelled or denied.'
        return
      }

      try {
        await this.completeGoogleOAuthLogin(code)
      } catch(err) {
        this.serverError = 'Google sign-in failed'
        if(err instanceof Error) {
          console.error('Google OAuth Callback Error:', err.message, err.stack)
        } else {
          console.error('Google OAuth Callback Error:', err)
        }
      }
    }
  },
  async created() {
    await this.configureUserType()
    await this.handleGoogleOAuthRedirect()
  }
}
</script>

<style scoped>
.oauth-callback-wrapper {
  display: flex;
  height: 100vh;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  background: var(--main-bg-color);
}

.error {
  color: #ff1744;
}
</style>
